import { useState, useEffect, useRef } from "react";
import { PayShareType } from "../types/payShareTypes";
import { usePayShareFeeCalculation } from "./usePayShareFeeCalculation";

export const usePayShareData = (
  students: any[],
  availableStages: any[],
  feetypes: any[],
  filters: {
    selectedFeeType: string;
    selectedStage: string;
    selectedSession: string;
    selectedCourseId: number | null;
  }
) => {
  const [payShareList, setPayShareList] = useState<PayShareType[]>([]);
  const { getFeeAndShareInfo } = usePayShareFeeCalculation(feetypes);
  const updateTimeoutRef = useRef<number | null>(null);
  const prevStudentsLengthRef = useRef<number>(0);

  // Transform student data to payShareList when students data changes
  useEffect(() => {
    // Clear any pending updates
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    // Skip update if nothing significant has changed
    if (students.length === 0 && prevStudentsLengthRef.current === 0) {
      return;
    }

    // Debounce the update to prevent multiple rapid updates
    updateTimeoutRef.current = setTimeout(() => {
      prevStudentsLengthRef.current = students.length;

      if (students.length > 0) {
        // Transform student data to payShareList format
        const transformedData: PayShareType[] = students.map((student) => {
          // Find fee type name from selected fee type ID using API data
          const selectedFeeTypeObj = feetypes.find(
            (ft) => ft.id.toString() === filters.selectedFeeType
          );
          const feeTypeName =
            selectedFeeTypeObj?.type === "coursefee"
              ? "Course Fee"
              : "Registration Fee";

          // Find stage name from selected stage ID
          const stageName = filters.selectedStage
            ? availableStages.find(
                (s) => s.id === Number(filters.selectedStage)
              )?.name || "-"
            : student.course?.stage?.name || "-";

          // Get fee and share information
          const { feeAmount, shareAmount, shareType, paymentDate } = getFeeAndShareInfo(
            student,
            filters.selectedFeeType
          );

          // Get payment status from centerPayments
          const paymentStatus = student.centerPayments?.some((payment: any) => {
            const isFeeTypeMatch =
              payment.fee_type ===
              (selectedFeeTypeObj?.type ||
                (filters.selectedFeeType === "1"
                  ? "coursefee"
                  : "registrationfee"));

            // For course fee, check stage_id match
            if (payment.fee_type === "coursefee") {
              return (
                isFeeTypeMatch &&
                payment.stage_id ===
                  Number(filters.selectedStage || student.course?.stage?.id) &&
                payment.status === "completed"
              );
            }

            // For registration fee, only check fee_type match
            return isFeeTypeMatch && payment.status === "completed";
          })
            ? "paid"
            : "pending";

          // Check if invoice exists for the selected fee type
          const hasInvoice = student.invoices?.some(
            (invoice: any) =>
              invoice.type ===
              (selectedFeeTypeObj?.type ||
                (filters.selectedFeeType === "1"
                  ? "coursefee"
                  : "registrationfee"))
          );

          return {
            id: student.id,
            student_id: student.profile?.studentid || `STU${student.id}`,
            student_name: student.name || "",
            session: student.session?.name || "-",
            course: student.course?.name || "-",
            center_name: student.center?.name || "-",
            fee_type: feeTypeName,
            stage: stageName,
            stage_id: student.course?.stage?.id || student.stage_id || 0,
            amount: feeAmount,
            share_amount: shareAmount,
            share_type: shareType || "fixed",
            payment_date: paymentDate,
            payment_status: paymentStatus,
            course_id: student.course?.id || student.course_id || 0,
            session_id: student.session?.id || student.session_id || 0,
          };
        });

        setPayShareList(transformedData);
      } else {
        setPayShareList([]);
      }
    }, 50); // Small delay to batch rapid updates

    // Cleanup function
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [
    students,
    filters.selectedFeeType,
    filters.selectedStage,
    filters.selectedSession,
    filters.selectedCourseId,
    availableStages,
    feetypes,
  ]);

  return {
    payShareList,
    setPayShareList,
  };
};
